import { createSelector } from '@reduxjs/toolkit';
import type { ShortHotelInfo } from 'src/components/YandexMap/types';
import { getIsMobile } from 'src/store/view/selectors';
import { getImmutableCompatible } from 'src/utils/immutableUtils';
import { isEmpty } from 'lodash';
import { getBrowser } from '../../selectors/browser';
import { viewedHotelsSelector } from '../../selectors/viewedHotels';
import { hotelsListSelector } from '../hotels/selectors';

import { name } from './slice';
import { hotelGeoPoint, regionGeoPoint } from './transform';
import type { MapState } from './types';
import { DEFAULT_MAP_POSITION, DEFAULT_SEARCH_ZOOM } from './constants';

export const mapSelector = (state): MapState =>
  getImmutableCompatible(state, name);

/**
 * Возвращает `bounds` для карты
 */
export const mapWithBoundsPositionSelector = createSelector(
  mapSelector,
  (state) => state.bounds,
);

/**
 * Возвращает `zoom` и `center` для карты, если они заданы в админке для региона
 */
const mapWithCenterPositionSelector = createSelector(mapSelector, (state) => {
  const { zoom, center } = state;

  if (zoom && center) {
    return {
      zoom: [zoom] as [number],
      center,
    };
  }
  return null;
});

/**
 * Возвращает координаты для отрисовки карты
 * `bounds` приходит всегда и используется для стран и мета-регионов
 */
export const mapPositionSelector = createSelector(
  [mapWithBoundsPositionSelector, mapWithCenterPositionSelector],
  (fitBounds, mapCenter) => {
    if (mapCenter) {
      return mapCenter;
    }
    if (fitBounds) {
      return { fitBounds };
    }
    return null;
  },
);

/**
 * Выбранный отель
 */
export const activeHotelSelector = createSelector(
  mapSelector,
  (state) => state.activeHotel,
);

/**
 * Регионы для карты
 */
export const regionsSelector = createSelector(
  mapSelector,
  (state) => state.regions,
);

/**
 * Регионы в виде geo-объектов для mapbox
 */
export const geoRegionsSelector = createSelector(regionsSelector, (regions) =>
  regions.map(regionGeoPoint),
);

/**
 * Свернута/развернута карта
 */
export const isExpandedMapSelector = createSelector(
  [mapSelector],
  (state) => state.expanded,
);

/**
 * Проверяет, нужно ли показывать кнопку раскрытия карты
 */
export const isExpandableMapSelector = createSelector(
  [getIsMobile, getBrowser],
  (isMobile, browser) => !isMobile && browser.lessThan.infinity,
);

export const isMapViewSelector = createSelector(
  [isExpandedMapSelector, getBrowser],
  (isExpanded) => isExpanded,
  // TODO: разобраться и вернуть проверку на && browser.lessThan.infinity (У меня почему-то не работает на маке)
);

export const popupDataSelector = createSelector(
  mapSelector,
  (state) => state.popupData,
);

export const isHotelCardOpenedSelector = createSelector(
  [popupDataSelector],
  (hotelInfo): boolean => !isEmpty(hotelInfo),
);

/**
 * Данные отеля, на который наведен курсор
 */
export const popupDataWithHotelInfoSelector = createSelector(
  [popupDataSelector, hotelsListSelector],
  (popupData, hotelsList) => {
    if (!popupData) {
      return undefined;
    }

    const popupHotelId = Number(popupData.id);
    const hotelInfo = hotelsList.find(({ id }) => id === popupHotelId);

    const result = {
      ...popupData,
      hotelInfo,
    };

    return result;
  },
);

export const hotelInfoFromPopupSelector = createSelector(
  [popupDataWithHotelInfoSelector],
  (popupData) => popupData?.hotelInfo,
);

/**
 * Отели в виде geo-объектов для mapbox
 */
export const mapHotelsListSelector = createSelector(
  [hotelsListSelector, viewedHotelsSelector],
  (hotelsList, viewedHotels) => {
    const transformHotels = hotelsList.map((hotelInfo) =>
      hotelGeoPoint({ hotelInfo, viewedHotels }),
    );

    return transformHotels;
  },
);

/**
 * Возвращает координаты для отрисовки карты Яндекса
 * `bounds` приходит всегда и используется для стран и мета-регионов
 */
export const yandexMapPositionSelector = createSelector(
  [mapWithBoundsPositionSelector, mapWithCenterPositionSelector],
  (fitBounds, mapCenter) => {
    if (mapCenter) {
      return { coordinates: mapCenter.center, zoom: mapCenter.zoom[0] };
    }
    if (fitBounds) {
      return { bounds: fitBounds, zoom: DEFAULT_SEARCH_ZOOM };
    }

    return { coordinates: DEFAULT_MAP_POSITION, zoom: DEFAULT_SEARCH_ZOOM };
  },
);

/**
 * Данные отеля, на который наведен курсор для карты Яндекса
 */
export const yandexMapActiveHotelShortInfo = createSelector(
  [activeHotelSelector, hotelsListSelector],
  (activeHotelInfo, hotelsList): ShortHotelInfo | undefined => {
    if (!activeHotelInfo) {
      return undefined;
    }

    const popupHotelId = Number(activeHotelInfo.id);
    const hotelInfo = hotelsList.find(({ id }) => id === popupHotelId);

    const result = {
      ...activeHotelInfo,
      state: 'active' as const,
      price: hotelInfo?.price || 0,
    };

    return result;
  },
);
